import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { WidgetProps } from '@/lib/widgets/types';
import { useSystemNotifications } from '@/hooks/use-notifications';
import { Bell, AlertCircle, Info, CheckCircle } from 'lucide-react';

export function NotificationsWidget({ isEditing = false }: WidgetProps) {
  const { data: notifications = [], isLoading } = useSystemNotifications();
  
  const recentNotifications = notifications.slice(0, 5);


  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'alert': 
      case 'warning': 
        return { variant: 'destructive' as const, className: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800' };
      case 'success': 
        return { variant: 'default' as const, className: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800' };
      case 'info': 
        return { variant: 'secondary' as const, className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800' };
      case 'system': 
        return { variant: 'outline' as const, className: 'bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-300 dark:border-cyan-800' };
      case 'announcement':
        return { variant: 'secondary' as const, className: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800' };
      default: 
        return { variant: 'outline' as const, className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600' };
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'alert': 
      case 'warning': return AlertCircle;
      case 'success': return CheckCircle;
      case 'info': return Info;
      case 'system': return Bell;
      case 'announcement': return Bell;
      default: return Bell;
    }
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notificações
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Bell className="h-4 w-4" />
          Notificações Recentes
          {notifications.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {notifications.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 pb-6">
        <ScrollArea className="h-full max-h-[200px]">
          {recentNotifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Nenhuma notificação recente
            </div>
          ) : (
            <div className="space-y-1">
              {recentNotifications.map((notification) => {
                const Icon = getNotificationIcon(notification.type);
                return (
                  <div
                    key={notification.id}
                    className="flex items-start gap-3 p-3 hover:bg-muted/50 transition-colors rounded-lg"
                  >
                    <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${getNotificationColor(notification.type).className.includes('red') ? 'text-red-500' : 
                      getNotificationColor(notification.type).className.includes('green') ? 'text-green-500' :
                      getNotificationColor(notification.type).className.includes('blue') ? 'text-blue-500' :
                      getNotificationColor(notification.type).className.includes('cyan') ? 'text-cyan-500' :
                      getNotificationColor(notification.type).className.includes('purple') ? 'text-purple-500' :
                      'text-muted-foreground'}`} />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium leading-none">
                          {notification.title}
                        </p>
                        <Badge 
                          variant={getNotificationColor(notification.type).variant} 
                          className={`text-xs ${getNotificationColor(notification.type).className}`}
                        >
                          {notification.type === 'system' ? 'Sistema' : 
                           notification.type === 'info' ? 'Info' :
                           notification.type === 'success' ? 'Sucesso' :
                           notification.type === 'alert' ? 'Alerta' :
                           notification.type === 'warning' ? 'Aviso' :
                           notification.type === 'announcement' ? 'Anúncio' :
                           notification.type}
                        </Badge>
                      </div>
                      {notification.content && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notification.content}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}